import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: '', amount: '', note: '', expiresOn: '', isOneTime: false });

  useEffect(() => {
    loadCoupons();
  }, []);

  const loadCoupons = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/coupons');
      setCoupons(data);
    } catch (err) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const createCoupon = async (e) => {
    e.preventDefault();
    const code = form.code.trim().toUpperCase();
    const amount = Number(form.amount);

    if (!code) return toast.error('Coupon code is required');
    if (!Number.isFinite(amount) || amount <= 0) return toast.error('Enter a valid amount');
    if (!form.expiresOn) return toast.error('Expiry date is required');

    setCreating(true);
    try {
      const { data } = await api.post('/coupons', {
        code,
        amount,
        note: form.note,
        expiresOn: form.expiresOn,
        isOneTime: form.isOneTime,
      });
      setCoupons(prev => [data, ...prev]);
      setForm({ code: '', amount: '', note: '', expiresOn: '', isOneTime: false });
      toast.success('Coupon created');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create coupon');
    } finally {
      setCreating(false);
    }
  };

  const toggleCoupon = async (id) => {
    try {
      const { data } = await api.patch(`/coupons/${id}/toggle`);
      setCoupons(prev => prev.map(c => (c.id === id ? data : c)));
      toast.success(`Coupon ${data.isActive ? 'activated' : 'deactivated'}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update coupon');
    }
  };

  const deleteCoupon = async (id, code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await api.delete(`/coupons/${id}`);
      setCoupons(prev => prev.filter(c => c.id !== id));
      toast.success('Coupon deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete coupon');
    }
  };

  const copyClaimLink = async (code) => {
    const link = `https://garhwalhikes.com/?coupon=1&code=${encodeURIComponent(code)}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Coupon claim link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const getStatusLabel = (c) => {
    if (!c.isActive) return 'Inactive';
    if (c.expiresAt && new Date(c.expiresAt).getTime() < Date.now()) return 'Expired';
    if (c.isOneTime && c.usedAt) return 'Used';
    return 'Active';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">🎟️ Coupons</h1>
        <button onClick={loadCoupons} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">
          Refresh
        </button>
      </div>

      <section className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Create Coupon</h2>
        <form onSubmit={createCoupon} className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <input
            type="text"
            placeholder="Code (e.g. GH500)"
            value={form.code}
            onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
          />
          <input
            type="number"
            min="1"
            placeholder="Amount"
            value={form.amount}
            onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
          />
          <input
            type="text"
            placeholder="Note (optional)"
            value={form.note}
            onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
          />
          <input
            type="date"
            value={form.expiresOn}
            onChange={e => setForm(f => ({ ...f, expiresOn: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500"
          />
          <label className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <input
              type="checkbox"
              checked={form.isOneTime}
              onChange={e => setForm(f => ({ ...f, isOneTime: e.target.checked }))}
            />
            One-time use
          </label>
          <button
            type="submit"
            disabled={creating}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create Coupon'}
          </button>
        </form>
      </section>

      <section className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Code</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Amount</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Note</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Expiry</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Used</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Created</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : coupons.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-gray-400">No coupons created yet</td></tr>
              ) : coupons.map(c => (
                <tr key={c.id}>
                  <td className="px-4 py-3 font-mono font-semibold text-gray-900">{c.code}</td>
                  <td className="px-4 py-3 text-gray-800">₹ {Number(c.amount).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3 text-gray-700">{c.isOneTime ? 'One-time' : 'Multi-use'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.note || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString('en-IN') : '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.isOneTime ? (c.usedAt ? '1/1' : '0/1') : (c.usedCount || 0)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getStatusLabel(c) === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                      {getStatusLabel(c)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{new Date(c.createdAt).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-3 flex gap-2">
                    <button
                      onClick={() => copyClaimLink(c.code)}
                      className="px-3 py-1 border rounded text-xs hover:bg-gray-50"
                    >
                      Copy Link
                    </button>
                    <button
                      onClick={() => toggleCoupon(c.id)}
                      className="px-3 py-1 border rounded text-xs hover:bg-gray-50"
                    >
                      {c.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => deleteCoupon(c.id, c.code)}
                      className="px-3 py-1 border border-red-300 text-red-600 rounded text-xs hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
